/**
 * 3D Reconstruction — Upload photos/video, run COLMAP, view result.
 */

import { api, $, toast } from './core';

let _reconPollInterval: ReturnType<typeof setInterval> | null = null;
let _reconCurrentJob: string | null = null;
let _reconInitialized = false;

export function loadReconstruction(): void {
    const sec = $('#sec-reconstruction');
    if (!sec) return;

    if (!_reconInitialized) {
        _reconInitialized = true;
        _bindEvents();
    }

    _loadJobs();
}

export function destroyReconstruction(): void {
    if (_reconPollInterval) { clearInterval(_reconPollInterval); _reconPollInterval = null; }
}

function _bindEvents(): void {
    const createBtn = $('#recon-btn-create');
    if (createBtn) createBtn.addEventListener('click', _reconCreate);

    const startBtn = $('#recon-btn-start');
    if (startBtn) startBtn.addEventListener('click', _reconStart);

    const fileInput = $('#recon-file-input') as HTMLInputElement | null;
    if (fileInput) fileInput.addEventListener('change', () => {
        if (fileInput.files) _uploadFiles(fileInput.files);
        fileInput.value = '';
    });
}

/* ── Job management ─────────────────────────────────────────── */

export async function _reconCreate(): Promise<void> {
    const nameEl = $('#recon-name') as HTMLInputElement;
    const name = nameEl?.value?.trim() || '';

    try {
        const resp = await api('/api/reconstruction/create', {
            method: 'POST',
            body: JSON.stringify({ name }),
        });

        if (resp.error) { toast(resp.error, 'error'); return; }

        _reconCurrentJob = resp.job_id;
        toast(`Proiect creat: ${resp.name}`);

        const uploadArea = $('#recon-upload-area') as HTMLElement | null;
        if (uploadArea) uploadArea.style.display = '';

        _setupDropZone();
        _loadJobs();
    } catch (e) {
        toast('Eroare la creare proiect', 'error');
    }
}

function _setupDropZone(): void {
    const dropZone = $('#recon-drop-zone');
    const fileInput = $('#recon-file-input') as HTMLInputElement | null;

    if (dropZone) {
        dropZone.addEventListener('click', () => fileInput?.click());
        dropZone.addEventListener('dragover', (e: Event) => {
            e.preventDefault();
            dropZone.classList.add('drag-over');
        });
        dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
        dropZone.addEventListener('drop', (e: Event) => {
            e.preventDefault();
            dropZone.classList.remove('drag-over');
            const dt = (e as DragEvent).dataTransfer;
            if (dt?.files) _uploadFiles(dt.files);
        });
    }
}

export function _reconFilesSelected(input: HTMLInputElement): void {
    if (input.files) _uploadFiles(input.files);
    input.value = '';
}

async function _uploadFiles(files: FileList): Promise<void> {
    if (!_reconCurrentJob || files.length === 0) return;

    const progressBar = $('#recon-upload-progress');
    const progressFill = $('#recon-progress-fill');
    const progressText = $('#recon-progress-text');
    const statsEl = $('#recon-upload-stats');
    if (progressBar) (progressBar as HTMLElement).style.display = '';

    const batchSize = 5;
    let uploaded = 0;
    const total = files.length;

    for (let i = 0; i < total; i += batchSize) {
        const formData = new FormData();
        for (let j = i; j < Math.min(i + batchSize, total); j++) {
            formData.append('files', files[j]);
        }

        try {
            const resp = await fetch(`/api/reconstruction/${_reconCurrentJob}/upload`, {
                method: 'POST',
                body: formData,
            });
            const data = await resp.json();
            uploaded += Math.min(batchSize, total - i);

            const pct = Math.round((uploaded / total) * 100);
            if (progressFill) (progressFill as HTMLElement).style.width = `${pct}%`;
            if (progressText) progressText.textContent = `${uploaded}/${total} fișiere`;

            if (statsEl && data.total_images) {
                statsEl.innerHTML = `<strong>${data.total_images}</strong> imagini totale` +
                    (data.video_frames ? ` (${data.video_frames} cadre din video)` : '');
            }
        } catch {
            toast('Eroare la upload', 'error');
        }
    }

    const startBtn = $('#recon-btn-start') as HTMLButtonElement | null;
    if (startBtn) startBtn.disabled = false;

    toast(`${total} fișiere încărcate`);
}

export async function _reconStart(): Promise<void> {
    if (!_reconCurrentJob) {
        toast('Creează mai întâi un proiect', 'error');
        return;
    }

    try {
        const resp = await api(`/api/reconstruction/${_reconCurrentJob}/start`, { method: 'POST' });
        if (resp.error) { toast(resp.error, 'error'); return; }

        toast('Reconstrucție pornită!');

        const uploadArea = $('#recon-upload-area');
        const processing = $('#recon-processing');
        if (uploadArea) (uploadArea as HTMLElement).style.display = 'none';
        if (processing) (processing as HTMLElement).style.display = '';

        _startPolling(_reconCurrentJob);
    } catch (e) {
        toast('Eroare la pornire', 'error');
    }
}

function _startPolling(jobId: string): void {
    if (_reconPollInterval) clearInterval(_reconPollInterval);

    _reconPollInterval = setInterval(async () => {
        try {
            const resp = await api(`/api/reconstruction/${jobId}/status`);
            _updatePipelineUI(resp);

            if (resp.status === 'completed' || resp.status === 'failed') {
                if (_reconPollInterval) { clearInterval(_reconPollInterval); _reconPollInterval = null; }
                if (resp.status === 'completed') {
                    toast('Reconstrucție completă!');
                    _load3DViewer(jobId);
                } else {
                    toast('Reconstrucție eșuată: ' + (resp.error || ''), 'error');
                }
                _loadJobs();
            }
        } catch { /* retry */ }
    }, 3000);
}

const PIPELINE_STEPS = [
    { key: 'uploading', label: 'Upload imagini', icon: '📤' },
    { key: 'features', label: 'Extragere features', icon: '🔍' },
    { key: 'matching', label: 'Potrivire imagini', icon: '🔗' },
    { key: 'sparse', label: 'Reconstrucție sparse', icon: '⭐' },
    { key: 'dense', label: 'Reconstrucție densă', icon: '🌫️' },
    { key: 'meshing', label: 'Generare mesh + GLB', icon: '🏗️' },
    { key: 'completed', label: 'Complet', icon: '✅' },
];

function _updatePipelineUI(data: any): void {
    const stepsEl = $('#recon-pipeline-steps');
    const logEl = $('#recon-log');
    if (!stepsEl) return;

    const currentIdx = PIPELINE_STEPS.findIndex(s => s.key === data.status);

    stepsEl.innerHTML = PIPELINE_STEPS.map((step, i) => {
        const cls = i < currentIdx ? 'done' : i === currentIdx ? 'active' : '';
        return `<div class="recon-step ${cls}">
            <span class="recon-step-icon">${step.icon}</span>
            <span class="recon-step-label">${step.label}</span>
            ${i === currentIdx ? '<span class="recon-step-spinner"></span>' : ''}
        </div>`;
    }).join('');

    let stats = '';
    if (data.total_images) stats += `Imagini: ${data.total_images} | `;
    if (data.sparse_points) stats += `Sparse: ${data.sparse_points.toLocaleString()} pts | `;
    if (data.dense_points) stats += `Dense: ${data.dense_points.toLocaleString()} pts | `;
    if (data.mesh_vertices) stats += `Mesh: ${data.mesh_vertices.toLocaleString()} verts, ${data.mesh_faces?.toLocaleString()} faces`;
    if (stats) stepsEl.innerHTML += `<div class="recon-stats">${stats}</div>`;

    if (logEl && data.log) {
        logEl.textContent = data.log;
        logEl.scrollTop = logEl.scrollHeight;
    }
}

/* ── 3D Viewer ──────────────────────────────────────────────── */

async function _load3DViewer(jobId: string): Promise<void> {
    const viewer = $('#recon-viewer');
    const placeholder = $('#recon-viewer-placeholder');
    if (!viewer) return;
    if (placeholder) (placeholder as HTMLElement).style.display = 'none';

    const canvas = document.createElement('div');
    canvas.id = 'recon-3d-canvas';
    canvas.style.cssText = 'width:100%;height:100%;';
    viewer.appendChild(canvas);

    if (!(window as any).THREE && (window as any)._loadThreeJS) {
        canvas.innerHTML = '<p style="padding:20px;color:var(--text-muted)">Se încarcă Three.js...</p>';
        await (window as any)._loadThreeJS();
        canvas.innerHTML = '';
    }
    const THREE = (window as any).THREE;
    if (!THREE) {
        canvas.innerHTML = '<p style="padding:20px;color:#ef4444">Three.js nu a putut fi încărcat.</p>';
        return;
    }

    const w = canvas.clientWidth || 600;
    const h = canvas.clientHeight || 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);

    const camera = new THREE.PerspectiveCamera(50, w / h, 0.01, 1000);
    camera.position.set(3, 3, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    canvas.appendChild(renderer.domElement);

    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 10, 5);
    scene.add(dirLight);

    scene.add(new THREE.GridHelper(20, 20, 0x444466, 0x222244));

    const loader = new THREE.GLTFLoader();
    loader.load(`/api/reconstruction/${jobId}/model`, (gltf: any) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 5 / maxDim;
        model.scale.setScalar(scale);
        model.position.sub(center.multiplyScalar(scale));
        scene.add(model);
        controls.target.set(0, 0, 0);
        camera.position.set(5, 4, 5);
        controls.update();
    }, undefined, (err: any) => {
        canvas.innerHTML += `<p style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#ef4444">
            Eroare: ${err?.message || 'model indisponibil'}</p>`;
    });

    function animate() {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
    }
    animate();

    const resizeObs = new ResizeObserver(() => {
        const nw = canvas.clientWidth;
        const nh = canvas.clientHeight;
        if (nw && nh) {
            camera.aspect = nw / nh;
            camera.updateProjectionMatrix();
            renderer.setSize(nw, nh);
        }
    });
    resizeObs.observe(canvas);
}

/* ── Jobs history ───────────────────────────────────────────── */

async function _loadJobs(): Promise<void> {
    const listEl = $('#recon-jobs-list');
    if (!listEl) return;

    try {
        const resp = await api('/api/reconstruction/jobs');
        const jobs = resp.jobs || [];

        if (jobs.length === 0) {
            listEl.innerHTML = '<p class="recon-empty">Nicio reconstrucție încă</p>';
            return;
        }

        listEl.innerHTML = jobs.map((j: any) => {
            const statusClass = j.status === 'completed' ? 'success' : j.status === 'failed' ? 'error' : 'processing';
            const statusLabel = j.status === 'completed' ? '✅ Complet' :
                j.status === 'failed' ? '❌ Eșuat' :
                j.status === 'uploading' ? '📤 Upload' : '⏳ ' + j.status;

            return `<div class="recon-job-card" data-job-id="${j.id}">
                <div class="recon-job-header">
                    <strong>${j.name}</strong>
                    <span class="recon-job-status ${statusClass}">${statusLabel}</span>
                </div>
                <div class="recon-job-meta">
                    ${j.total_images || 0} imagini |
                    ${j.mesh_vertices ? j.mesh_vertices.toLocaleString() + ' verts' : 'no mesh'} |
                    ${j.created_at || ''}
                </div>
            </div>`;
        }).join('');

        listEl.querySelectorAll('.recon-job-card').forEach((card: Element) => {
            card.addEventListener('click', () => {
                const jobId = (card as HTMLElement).dataset.jobId;
                if (jobId) _reconViewJob(jobId);
            });
        });
    } catch {
        listEl.innerHTML = '<p class="recon-empty">Eroare la încărcare</p>';
    }
}

export function _reconViewJob(jobId: string): void {
    _reconCurrentJob = jobId;
    _load3DViewer(jobId);
}
